import { Controller } from "@hotwired/stimulus"

// Manages row selection and bulk actions for the log results table.
// Mounted on .siem-bulk-bar; listens via document event delegation so it
// works with dynamically Turbo-streamed table rows.
export default class extends Controller {
  static targets = ["count"]
  static values = { investigationsUrl: String }

  #selected = new Set()
  #boundChange = null
  #boundTurbo = null
  #addController = null

  connect() {
    this.#boundChange = e => this.#onCheckboxChange(e)
    this.#boundTurbo = () => this.#syncSelectAll()
    document.addEventListener("change", this.#boundChange)
    document.addEventListener("turbo:frame-render", this.#boundTurbo)
  }

  disconnect() {
    document.removeEventListener("change", this.#boundChange)
    document.removeEventListener("turbo:frame-render", this.#boundTurbo)
    this.#cancelAdd()
  }

  clearAll() {
    this.#selected.clear()
    document.querySelectorAll(".siem-row-checkbox, .siem-select-all").forEach(cb => { cb.checked = false })
    this.#syncSelectAll()
    this.#update()
  }

  copyUuids() {
    if (!this.#selected.size) return
    navigator.clipboard?.writeText([...this.#selected].join("\n")).catch(() => {})
  }

  async addToInvestigation() {
    if (!this.#selected.size || !this.investigationsUrlValue) return

    const investigations = await this.#fetchInvestigations()
    if (!investigations?.length) {
      this.#toast("No investigations found. Create one via the Notebook first.")
      return
    }

    this.#showPicker(investigations)
  }

  #showPicker(investigations) {
    const n = this.#selected.size
    const overlay = document.createElement("div")
    overlay.className = "siem-inv-picker-overlay"
    overlay.innerHTML = `
      <div class="siem-inv-picker" role="dialog" aria-modal="true">
        <div class="siem-inv-picker-hdr">
          <span>Add ${n} log${n !== 1 ? "s" : ""} to investigation</span>
          <button class="siem-inv-picker-close" type="button" aria-label="Close">×</button>
        </div>
        <div class="siem-inv-picker-list">
          ${investigations.map(inv => `
            <button class="siem-inv-picker-card" type="button"
                    data-inv-id="${inv.id}"
                    data-inv-name="${this.#esc(inv.name)}">
              <span class="siem-inv-picker-name">${this.#esc(inv.name)}</span>
              <span class="siem-inv-picker-meta">${inv.log_count} log${inv.log_count !== 1 ? "s" : ""}</span>
            </button>
          `).join("")}
        </div>
        <div class="siem-inv-picker-status hidden"></div>
      </div>
    `

    const close = () => {
      this.#cancelAdd()
      overlay.remove()
    }
    overlay.querySelector(".siem-inv-picker-close").addEventListener("click", close)
    overlay.addEventListener("click", e => { if (e.target === overlay) close() })
    document.addEventListener("keydown", e => { if (e.key === "Escape") close() }, { once: true })

    overlay.querySelectorAll(".siem-inv-picker-card").forEach(card => {
      card.addEventListener("click", async () => {
        const invId = card.dataset.invId
        const invName = card.dataset.invName
        this.#cancelAdd()
        this.#addController = new AbortController()
        const { signal } = this.#addController

        overlay.querySelectorAll(".siem-inv-picker-card").forEach(c => { c.disabled = true })
        card.classList.add("siem-inv-picker-card--loading")
        const statusEl = overlay.querySelector(".siem-inv-picker-status")
        statusEl.textContent = "Adding…"
        statusEl.classList.remove("hidden")

        const added = await this.#doAdd(invId, signal)
        if (signal.aborted) return

        card.classList.remove("siem-inv-picker-card--loading")
        statusEl.textContent = `Added ${added} of ${n} log${n !== 1 ? "s" : ""} to "${invName}"`
        statusEl.classList.add("siem-inv-picker-status--done")
        this.clearAll()
        setTimeout(close, 1400)
      })
    })

    document.body.appendChild(overlay)
  }

  async #doAdd(invId, signal) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const uuids = [...this.#selected]
    const results = await Promise.all(uuids.map(async uuid => {
      try {
        const body = new URLSearchParams({ log_uuid: uuid, authenticity_token: csrf })
        const resp = await fetch(`${this.investigationsUrlValue}/${invId}/items`, {
          method: "POST", body, signal,
          headers: { Accept: "application/json", "X-CSRF-Token": csrf },
        })
        return resp.ok
      } catch { return false }
    }))
    return results.filter(Boolean).length
  }

  #cancelAdd() {
    this.#addController?.abort()
    this.#addController = null
  }

  #toast(msg) {
    const el = document.createElement("div")
    el.className = "siem-inv-toast"
    el.textContent = msg
    document.body.appendChild(el)
    setTimeout(() => el.remove(), 3000)
  }

  #esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }

  #onCheckboxChange(e) {
    const cb = e.target
    if (cb.classList.contains("siem-select-all")) {
      this.#toggleAll(cb.checked)
      return
    }
    if (!cb.classList.contains("siem-row-checkbox")) return
    const uuid = cb.dataset.logUuid
    if (!uuid) return
    cb.checked ? this.#selected.add(uuid) : this.#selected.delete(uuid)
    this.#syncSelectAll()
    this.#update()
  }

  #toggleAll(checked) {
    document.querySelectorAll(".siem-row-checkbox").forEach(cb => {
      const uuid = cb.dataset.logUuid
      if (!uuid) return
      cb.checked = checked
      checked ? this.#selected.add(uuid) : this.#selected.delete(uuid)
    })
    this.#update()
  }

  #syncSelectAll() {
    const all = document.querySelectorAll(".siem-row-checkbox")
    const checked = [...all].filter(cb => cb.checked)
    const sa = document.querySelector(".siem-select-all")
    if (!sa) return
    sa.checked = all.length > 0 && checked.length === all.length
    sa.indeterminate = checked.length > 0 && checked.length < all.length
  }

  #update() {
    const n = this.#selected.size
    if (this.hasCountTarget) this.countTarget.textContent = `${n} selected`
    this.element.classList.toggle("hidden", n === 0)
  }

  async #fetchInvestigations() {
    try {
      const resp = await fetch(this.investigationsUrlValue, { headers: { Accept: "application/json" } })
      return await resp.json()
    } catch { return [] }
  }
}
